import fetcher from './fetcher';
import { useCommonAuthProvider } from '@modules/auth/providers/CommonAuthProvider/CommonAuthProvider';

import * as Sentry from '@sentry/react';
import { StatusCodes } from 'http-status-codes';
import { CRIBL_AGENT_HEADER_NAME, MAESTRO_AGENT } from 'shared/src/observability/constants';

export type AuthenticatedFetcher = <T>(url: string, options?: RequestInit) => Promise<T>;

type UseFetcherHook = {
  fetcher: AuthenticatedFetcher;
};

const useFetcher = (): UseFetcherHook => {
  const auth = useCommonAuthProvider();
  const zeusBaseUrl = process.env.ZEUS_API_URL as string;

  const authenticatedFetcher: AuthenticatedFetcher = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
    try {
      const token = await auth.getAccessToken();
      // Maestro should send the x-cribl-agent header to Zeus to help with attribution on metrics. 
      // At the moment, the leader does not support this header over CORS so we should make sure not to send it there
      const targetSpecificHeaders: Record<string, string> = url.startsWith(zeusBaseUrl)
        ? { [CRIBL_AGENT_HEADER_NAME]: MAESTRO_AGENT }
        : {};

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...targetSpecificHeaders,
        ...(options.headers || {}),
      };

      return await fetcher<T>(url, { ...options, headers });
    } catch (error: any) {
      if (
        error?.status === StatusCodes.UNAUTHORIZED ||
        error?.error === 'login_required' ||
        error?.error === 'invalid_grant'
      ) {
        Sentry.captureException(error, {
          contexts: {
            api: {
              name: 'AuthenticatedFetcher',
              url,
              status: error?.status,
              error: error?.error,
            },
          },
        });
      }

      throw error;
    }
  };

  return { fetcher: authenticatedFetcher };
};

export default useFetcher;
